// src/pages/Planning.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/fr';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, Grid, InputLabel, MenuItem, Paper, Select,
  Stack, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Class as CourseIcon, Event as EventIcon,
  LocationOn as LocationIcon, Person as PersonIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const COLORS = {
  course: '#2563EB', event: '#16A34A', workshop: '#F97316',
  audition: '#7C3AED', meeting: '#64748B',
};

const text = (value = '') => String(value).trim().toLowerCase();
const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const addMinutes = (date, minutes = 60) => new Date(date.getTime() + Number(minutes) * 60000);
const overlaps = (a, b) => a.start < b.end && a.end > b.start;

const eventType = (event) => {
  const category = text(event.category);
  if (category.includes('workshop') || category.includes('stage')) return 'workshop';
  if (category.includes('audition') || category.includes('casting')) return 'audition';
  if (category.includes('réunion') || category.includes('reunion') || category.includes('meeting')) return 'meeting';
  return 'event';
};

const teacherNames = (teachers = []) => teachers.map((teacher) => {
  if (typeof teacher === 'string') return teacher;
  return teacher?.pseudo || `${teacher?.firstName || ''} ${teacher?.lastName || ''}`.trim() || teacher?.id || '';
}).filter(Boolean);

const teacherIds = (teachers = []) => teachers.map((teacher) =>
  typeof teacher === 'string' ? teacher : teacher?.id || teacher?.pseudo
).filter(Boolean);

export default function Planning() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selected, setSelected] = useState(null);
  const [slot, setSlot] = useState(null);
  const [teacher, setTeacher] = useState('');
  const [location, setLocation] = useState('');
  const [visibleTypes, setVisibleTypes] = useState({
    course: true, event: true, workshop: true, audition: true, meeting: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const [courseSnap, eventSnap] = await Promise.all([
          getDocs(collection(db, 'dance_courses')),
          getDocs(collection(db, 'events')),
        ]);
        setCourses(courseSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setEvents(eventSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error(err);
        setError("Impossible de charger le planning. Vérifie la connexion et les règles Firestore.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = useMemo(() => {
    const courseItems = courses.flatMap((course) => (course.dates || []).map((entry, index) => {
      const start = toDate(entry?.date);
      if (!start) return null;
      let end = toDate(entry?.endDate);
      if (!end && entry?.endTime) {
        const [hours, minutes] = entry.endTime.split(':').map(Number);
        end = new Date(start);
        end.setHours(hours || 0, minutes || 0, 0, 0);
      }
      if (!end || end <= start) end = addMinutes(start, entry?.duration || course.duration || 60);
      return {
        id: `course-${course.id}-${index}`,
        sourceId: course.id,
        sourceType: 'course',
        type: 'course',
        title: course.title || 'Cours sans titre',
        start, end, allDay: false,
        teachers: teacherNames(course.teachers),
        teacherIds: teacherIds(course.teachers),
        locationName: course.location?.name || course.lieu || '',
        locationAddress: course.location?.address || '',
        participantsCount: course.enrolledStudents?.length || course.participantsCount || 0,
        maxStudents: Number(course.maxStudents) || null,
      };
    }).filter(Boolean));

    const eventItems = events.map((event) => {
      const start = toDate(event.startDate);
      if (!start) return null;
      let end = toDate(event.endDate);
      if (!end || end <= start) end = addMinutes(start, 60);
      return {
        id: `event-${event.id}`,
        sourceId: event.id,
        sourceType: 'event',
        type: eventType(event),
        title: event.title || 'Événement sans titre',
        start, end, allDay: Boolean(event.allDay),
        teachers: [], teacherIds: [],
        locationName: event.location?.name || event.location || '',
        locationAddress: event.location?.address || '',
        participantsCount: Number(event.participantsCount) || 0,
        maxStudents: null,
      };
    }).filter(Boolean);

    const all = [...courseItems, ...eventItems];
    return all.map((item) => {
      const conflicts = all.filter((other) => {
        if (other.id === item.id || !overlaps(item, other)) return false;
        const sameLocation = item.locationName && other.locationName && text(item.locationName) === text(other.locationName);
        const sameTeacher = item.teacherIds.length > 0 && other.teacherIds.some((id) => item.teacherIds.includes(id));
        return sameLocation || sameTeacher;
      });
      return { ...item, conflicts, hasConflict: conflicts.length > 0 };
    });
  }, [courses, events]);

  const teachers = useMemo(() => [...new Set(items.flatMap((item) => item.teachers))].sort(), [items]);
  const locations = useMemo(() => [...new Set(items.map((item) => item.locationName).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => items.filter((item) => {
    if (!visibleTypes[item.type]) return false;
    if (teacher && !item.teachers.includes(teacher)) return false;
    if (location && item.locationName !== location) return false;
    return true;
  }), [items, visibleTypes, teacher, location]);

  const stats = useMemo(() => ({
    total: filtered.length,
    courses: filtered.filter((item) => item.type === 'course').length,
    events: filtered.filter((item) => item.type !== 'course').length,
    conflicts: filtered.filter((item) => item.hasConflict).length,
  }), [filtered]);

  const queryForSlot = () => {
    if (!slot?.start) return '';
    const start = moment(slot.start);
    const end = moment(slot.end || slot.start).isAfter(start) ? moment(slot.end) : moment(start).add(1, 'hour');
    return new URLSearchParams({
      date: start.format('YYYY-MM-DD'),
      startTime: start.format('HH:mm'),
      endTime: end.format('HH:mm'),
    }).toString();
  };

  if (loading) return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <Stack alignItems="center" spacing={2}><CircularProgress /><Typography>Chargement du planning...</Typography></Stack>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Planning MKD</Typography>
          <Typography color="text.secondary">Vue globale des cours et événements</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<CourseIcon />} onClick={() => navigate('/courses/new')}>Nouveau cours</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/events/new')}>Nouvel événement</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[['Éléments', stats.total], ['Cours', stats.courses], ['Événements', stats.events], ['Conflits', stats.conflicts]].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h5" fontWeight={800}>{value}</Typography></Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {[
                ['course', 'Cours'], ['event', 'Événements'], ['workshop', 'Workshops'],
                ['audition', 'Auditions'], ['meeting', 'Réunions'],
              ].map(([key, label]) => (
                <FormControlLabel key={key} control={<Checkbox checked={visibleTypes[key]} onChange={(e) => setVisibleTypes((prev) => ({ ...prev, [key]: e.target.checked }))} />} label={label} />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <FormControl fullWidth size="small"><InputLabel>Professeur</InputLabel><Select label="Professeur" value={teacher} onChange={(e) => setTeacher(e.target.value)}><MenuItem value="">Tous</MenuItem>{teachers.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</Select></FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <FormControl fullWidth size="small"><InputLabel>Salle / lieu</InputLabel><Select label="Salle / lieu" value={location} onChange={(e) => setLocation(e.target.value)}><MenuItem value="">Tous</MenuItem>{locations.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</Select></FormControl>
          </Grid>
        </Grid>
      </Paper>

      {stats.conflicts > 0 && <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>Les créneaux en rouge présentent un conflit de salle ou de professeur.</Alert>}

      <Paper sx={{ p: { xs: 1, md: 2 }, borderRadius: 3, overflowX: 'auto', '& .rbc-calendar': { minWidth: { xs: 850, md: 0 } }, '& .rbc-toolbar': { gap: 1, flexWrap: 'wrap' }, '& .rbc-today': { backgroundColor: '#EFF6FF' } }}>
        <Calendar
          localizer={localizer}
          events={filtered}
          date={date}
          view={view}
          onNavigate={setDate}
          onView={setView}
          views={['month', 'week', 'day', 'agenda']}
          startAccessor="start"
          endAccessor="end"
          allDayAccessor="allDay"
          selectable
          popup
          step={30}
          timeslots={2}
          min={new Date(1970, 0, 1, 7, 0, 0)}
          max={new Date(1970, 0, 1, 23, 0, 0)}
          style={{ height: '72vh', minHeight: 650 }}
          onSelectEvent={setSelected}
          onSelectSlot={setSlot}
          eventPropGetter={(item) => ({ style: { backgroundColor: item.hasConflict ? '#DC2626' : COLORS[item.type], border: 'none', borderRadius: 6, color: '#fff' } })}
          messages={{ today: "Aujourd'hui", previous: 'Précédent', next: 'Suivant', month: 'Mois', week: 'Semaine', day: 'Jour', agenda: 'Agenda', date: 'Date', time: 'Heure', event: 'Élément', noEventsInRange: 'Aucun élément sur cette période.' }}
        />
      </Paper>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        {selected && <>
          <DialogTitle><Stack direction="row" spacing={1} alignItems="center">{selected.sourceType === 'course' ? <CourseIcon color="primary" /> : <EventIcon color="success" />}<Box><Typography variant="h6" fontWeight={800}>{selected.title}</Typography><Chip size="small" label={selected.sourceType === 'course' ? 'Cours' : 'Événement'} /></Box></Stack></DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography>{moment(selected.start).format('dddd D MMMM YYYY')} · {selected.allDay ? 'Toute la journée' : `${moment(selected.start).format('HH:mm')}–${moment(selected.end).format('HH:mm')}`}</Typography>
              {selected.locationName && <Stack direction="row" spacing={1}><LocationIcon fontSize="small" /><Typography>{selected.locationName}</Typography></Stack>}
              {selected.teachers.length > 0 && <Stack direction="row" spacing={1}><PersonIcon fontSize="small" /><Typography>{selected.teachers.join(', ')}</Typography></Stack>}
              {selected.sourceType === 'course' && <Typography><strong>Inscrits :</strong> {selected.participantsCount}{selected.maxStudents ? ` / ${selected.maxStudents}` : ''}</Typography>}
              {selected.hasConflict && <Alert severity="warning">Conflit avec : {selected.conflicts.map((item) => item.title).join(', ')}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelected(null)}>Fermer</Button>
            <Button variant="outlined" onClick={() => navigate(selected.sourceType === 'course' ? `/courses/${selected.sourceId}` : `/events/${selected.sourceId}`)}>Voir</Button>
            <Button variant="contained" onClick={() => navigate(selected.sourceType === 'course' ? `/courses/edit/${selected.sourceId}` : `/events/${selected.sourceId}/edit`)}>Modifier</Button>
          </DialogActions>
        </>}
      </Dialog>

      <Dialog open={Boolean(slot)} onClose={() => setSlot(null)} fullWidth maxWidth="xs">
        <DialogTitle>Créer depuis ce créneau</DialogTitle>
        <DialogContent>{slot?.start && <Alert severity="info" sx={{ mt: 1 }}>{moment(slot.start).format('dddd D MMMM YYYY à HH:mm')}</Alert>}</DialogContent>
        <DialogActions>
          <Button onClick={() => setSlot(null)}>Annuler</Button>
          <Button variant="outlined" onClick={() => navigate(`/courses/new?${queryForSlot()}`)}>Cours</Button>
          <Button variant="contained" onClick={() => navigate(`/events/new?${queryForSlot()}`)}>Événement</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
